/**
 * Clear all contracts cache entries (for all pages/queries)
 */
export const clearAllContractsCache = async () => {
  try {
    const db = await initContractsCacheDB();
    const transaction = db.transaction([CONTRACTS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(CONTRACTS_CACHE_STORE);
    return new Promise((resolve, reject) => {
      const request = store.openCursor();
      let deletedCount = 0;
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const key = cursor.key;
          if (typeof key === 'string' && key.startsWith('contracts_')) {
            cursor.delete();
            deletedCount++;
          }
          cursor.continue();
        } else {
          resolve(deletedCount);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error clearing all contracts cache:', error);
    return 0;
  }
};
/**
 * Get contracts data from IndexedDB cache
 */
export const getContractsCache = async (cacheKey) => {
  try {
    const db = await initContractsCacheDB();
    const transaction = db.transaction([CONTRACTS_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(CONTRACTS_CACHE_STORE);
    return new Promise((resolve, reject) => {
      const request = store.get(cacheKey);
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({
            contracts: result.contracts,
            pagination: result.pagination,
            timestamp: result.timestamp,
          });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting contracts cache:', error);
    return null;
  }
};

/**
 * Generate cache key from query parameters for contracts
 */
export const generateContractsCacheKey = (params) => {
  const { page, per_page, search, project_id, customer_id, status } = params;
  return `contracts_${page || 1}_${per_page || 10}_${search || ''}_${project_id || ''}_${customer_id || ''}_${status || ''}`;
};
/**
 * Store name for contracts cache
 */
const CONTRACTS_CACHE_STORE = 'contractsCache';

/**
 * Initialize contracts cache store
 */
const initContractsCacheDB = async () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('OfflineContractsDB', 1);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(CONTRACTS_CACHE_STORE)) {
        const store = db.createObjectStore(CONTRACTS_CACHE_STORE, { keyPath: 'cacheKey' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

/**
 * Save contracts data to IndexedDB cache
 */
export const saveContractsCache = async (cacheKey, contractsData, pagination) => {
  try {
    const db = await initContractsCacheDB();
    const transaction = db.transaction([CONTRACTS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(CONTRACTS_CACHE_STORE);
    // Ensure contractsData is always an array
    const contractsArray = Array.isArray(contractsData) ? contractsData : [];
    const cacheData = {
      cacheKey,
      contracts: contractsArray,
      pagination,
      timestamp: Date.now(),
    };
    return new Promise((resolve, reject) => {
      const request = store.put(cacheData);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error saving contracts cache:', error);
    throw error;
  }
};

/**
 * Get all contracts cache entries for a specific query (all pages)
 */
export const getAllContractsCacheForQuery = async (params = {}) => {
  try {
    const db = await initContractsCacheDB();
    const transaction = db.transaction([CONTRACTS_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(CONTRACTS_CACHE_STORE);
    
    // If specific filters are provided, we should ideally filter
    // But for simplicity and robustness (especially for finding by ID), 
    // we'll get all contracts and then filter if needed.
    const { search, project_id, customer_id, status } = params;
    
    return new Promise((resolve, reject) => {
      const allContracts = [];
      const request = store.openCursor();
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const key = cursor.key;
          if (typeof key === 'string' && key.startsWith('contracts_')) {
            const contracts = cursor.value.contracts || [];
            if (Array.isArray(contracts)) {
              allContracts.push(...contracts);
            }
          }
          cursor.continue();
        } else {
          // Deduplicate by ID
          const uniqueMap = new Map();
          allContracts.forEach(c => {
            if (c && c.id) uniqueMap.set(String(c.id), c);
          });
          
          let resultItems = Array.from(uniqueMap.values());
          
          // Apply basic filtering if params are provided
          if (search) {
            const s = String(search).toLowerCase();
            resultItems = resultItems.filter(c => 
              (c.contract_no && String(c.contract_no).toLowerCase().includes(s)) ||
              (c.notes && String(c.notes).toLowerCase().includes(s))
            );
          }
          if (project_id) {
            resultItems = resultItems.filter(c => String(c.project_id) === String(project_id));
          }
          if (customer_id) {
            resultItems = resultItems.filter(c => String(c.customer_id) === String(customer_id));
          }
          if (status) {
            resultItems = resultItems.filter(c => String(c.status) === String(status));
          }

          resolve({ items: resultItems, pagination: null });
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting all contracts cache:', error);
    return { items: [], pagination: null };
  }
};
/**
 * IndexedDB utility for storing and managing offline requests
 * This utility handles saving failed requests and retrying them when network is available
 */

const DB_NAME = 'OfflineRequestsDB';
const DB_VERSION = 1;
const STORE_NAME = 'pendingRequests';

/**
 * Initialize IndexedDB database
 */
const initDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const objectStore = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        objectStore.createIndex('timestamp', 'timestamp', { unique: false });
        objectStore.createIndex('status', 'status', { unique: false });
      }
    };
  });
};

/**
 * Convert FormData to a serializable object
 * If formData is null/undefined (e.g. DELETE or empty PATCH), return empty data structure
 */
const formDataToObject = async (formData) => {
  // Support requests without body (DELETE, some PATCH, etc.)
  if (!formData) {
    return { data: {}, files: [], bodyType: 'none' };
  }

  // If a plain object was passed (JSON body), store it directly
  if (typeof formData === 'object' && !(formData instanceof FormData)) {
    return { data: formData, files: [], bodyType: 'json' };
  }

  // Otherwise treat as FormData
  const obj = {};
  const files = [];
  
  for (const [key, value] of formData.entries()) {
    if (value instanceof File) {
      const base64 = await fileToBase64(value);
      files.push({
        key,
        name: value.name,
        type: value.type,
        size: value.size,
        data: base64,
      });
    } else {
      const cleanKey = key.replace('[]', '');
      if (key.includes('[]')) {
        if (!obj[cleanKey]) obj[cleanKey] = [];
        obj[cleanKey].push(value);
      } else {
        obj[key] = value;
      }
    }
  }
  
  return { data: obj, files, bodyType: 'form' };
};

/**
 * Convert base64 back to File
 */
const base64ToFile = (base64, fileName, mimeType) => {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  return new File([byteArray], fileName, { type: mimeType });
};

/**
 * Convert File to base64
 */
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      // Remove data:image/png;base64, prefix
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

/**
 * Convert serialized object back to FormData
 */
const objectToFormData = (obj) => {
  // If object is null/undefined, or no data/files -> return null (no body)
  if (!obj) {
    return null;
  }

  // If original body type was JSON, don't build FormData here
  if (obj.bodyType === 'json') {
    return null;
  }

  if ((!obj.data || Object.keys(obj.data).length === 0) && (!obj.files || obj.files.length === 0)) {
    return null;
  }
  
  const formData = new FormData();
  
  // Add regular fields
  Object.keys(obj.data || {}).forEach(key => {
    const value = obj.data[key];
    if (Array.isArray(value)) {
      value.forEach(item => formData.append(`${key}[]`, item));
    } else {
      formData.append(key, value);
    }
  });
  
  // Add files
  if (obj.files && Array.isArray(obj.files)) {
    obj.files.forEach(fileInfo => {
      const file = base64ToFile(fileInfo.data, fileInfo.name, fileInfo.type);
      formData.append(fileInfo.key, file);
    });
  }
  
  return formData;
};

/**
 * Save a failed request to IndexedDB
 */
export const saveRequest = async (requestData) => {
  try {
    // Convert FormData to serializable object first (before opening transaction)
    const serializedData = await formDataToObject(requestData.formData);
    // Ensure serializedData is not undefined
    const validSerializedData = serializedData || { data: {}, files: [] };
    const serializedDataString = JSON.stringify(validSerializedData);
    
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const statusIndex = store.index('status');
    
    // Check for duplicates within the same transaction
    return new Promise((resolve, reject) => {
      const pendingRequests = [];
      const openCursor = statusIndex.openCursor(IDBKeyRange.only('pending'));
      
      openCursor.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          pendingRequests.push(cursor.value);
          cursor.continue();
        } else {
          // Finished reading all pending requests, now check for duplicates
          const duplicateRequest = pendingRequests.find(req => {
            // Check URL and method
            if (req.url !== requestData.url || req.method !== (requestData.method || 'POST')) {
              return false;
            }
            
            // Also check if the data content is the same
            const reqDataString = JSON.stringify(req.serializedData);
            return reqDataString === serializedDataString;
          });
          
          if (duplicateRequest) {
            // Return existing request ID (request is already saved, so it's fine)
            resolve(duplicateRequest.id);
            return;
          }
          
          // No duplicate found, add the new request
          const requestObj = {
            url: requestData.url,
            method: requestData.method || 'POST',
            headers: requestData.headers || {},
            serializedData,
            timestamp: Date.now(),
            status: 'pending',
            retryCount: 0,
          };
          
          const addRequest = store.add(requestObj);
          
          addRequest.onsuccess = () => {
            resolve(addRequest.result);
          };
          addRequest.onerror = () => {
            reject(addRequest.error);
          };
        }
      };
      
      openCursor.onerror = () => {
        reject(openCursor.error);
      };
      
      
      
      transaction.onerror = () => {
        console.error('Transaction error:', transaction.error);
        reject(transaction.error);
      };
    });
  } catch (error) {
    console.error('Error saving request to IndexedDB:', error);
    throw error;
  }
};

/**
 * Get all pending requests from IndexedDB
 */
export const getPendingRequests = async () => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('status');
    
    return new Promise((resolve, reject) => {
      const requests = [];
      const openCursor = index.openCursor(IDBKeyRange.only('pending'));
      
      openCursor.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          requests.push(cursor.value);
          cursor.continue();
        } else {
          resolve(requests);
        }
      };
      
      openCursor.onerror = () => reject(openCursor.error);
    });
  } catch (error) {
    console.error('Error getting pending requests:', error);
    return [];
  }
};

/**
 * Delete a request from IndexedDB after successful retry
 */
export const deleteRequest = async (id) => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const deleteRequest = store.delete(id);
      deleteRequest.onsuccess = () => resolve();
      deleteRequest.onerror = () => reject(deleteRequest.error);
    });
  } catch (error) {
    console.error('Error deleting request:', error);
    throw error;
  }
};

/**
 * Update request status
 */
export const updateRequestStatus = async (id, status, retryCount = 0) => {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    
    return new Promise((resolve, reject) => {
      const getRequest = store.get(id);
      
      getRequest.onsuccess = () => {
        const data = getRequest.result;
        if (data) {
          data.status = status;
          data.retryCount = retryCount;
          const updateRequest = store.put(data);
          updateRequest.onsuccess = () => resolve();
          updateRequest.onerror = () => reject(updateRequest.error);
        } else {
          resolve();
        }
      };
      
      getRequest.onerror = () => reject(getRequest.error);
    });
  } catch (error) {
    console.error('Error updating request status:', error);
    throw error;
  }
};

/**
 * Check if network is online
 */
export const isOnline = () => {
  const online = navigator.onLine;

  return online;
};

/**
 * Retry a stored request
 */
export const retryRequest = async (storedRequest) => {
  try {
    const isJson = storedRequest.serializedData?.bodyType === 'json';
    const formData = objectToFormData(storedRequest.serializedData);
    
    const headers = { ...storedRequest.headers };
    const fetchOptions = { method: storedRequest.method, headers };

    if (isJson) {
      // Send JSON body
      headers['Content-Type'] = 'application/json';
      const jsonBody = JSON.stringify(storedRequest.serializedData?.data || {});
      fetchOptions.body = jsonBody;
    } else {
      // When sending FormData, don't set Content-Type - browser will set it automatically with boundary
      if (formData && headers['Content-Type']) {
        delete headers['Content-Type'];
      }
      if (formData) {
        fetchOptions.body = formData;
      }
    }
    
    const response = await fetch(storedRequest.url, fetchOptions);
    
    if (!response.ok) {
      // Check if it's a client error (4xx) - these should not be retried
      // 400: Bad Request, 401: Unauthorized, 403: Forbidden, 404: Not Found
      if (response.status >= 400 && response.status < 500) {
        const error = new Error(`HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.isClientError = true; // Mark as client error to prevent retry
        throw error;
      }
      // Server errors (5xx) or other errors can be retried
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    // DELETE requests might return empty response
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      return await response.json();
    } else {
      return { success: true, message: 'Request completed successfully' };
    }
  } catch (error) {
    console.error('Error retrying request:', error);
    throw error;
  }
};

/**
 * Get count of pending requests
 */
export const getPendingRequestsCount = async () => {
  const requests = await getPendingRequests();
  return requests.length;
};

/**
 * Store name for tasks cache
 */
const TASKS_CACHE_STORE = 'tasksCache';

/**
 * Initialize tasks cache store
 */
const initTasksCacheDB = async () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('OfflineTasksDB', 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(TASKS_CACHE_STORE)) {
        const store = db.createObjectStore(TASKS_CACHE_STORE, { keyPath: 'cacheKey' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

/**
 * Save tasks data to IndexedDB cache
 */
export const saveTasksCache = async (cacheKey, tasksData, pagination) => {
  try {
    const db = await initTasksCacheDB();
    const transaction = db.transaction([TASKS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(TASKS_CACHE_STORE);
    
    const cacheData = {
      cacheKey,
      tasks: tasksData,
      pagination,
      timestamp: Date.now(),
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(cacheData);
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    throw error;
  }
};

/**
 * Get tasks data from IndexedDB cache
 */
export const getTasksCache = async (cacheKey) => {
  try {
    const db = await initTasksCacheDB();
    const transaction = db.transaction([TASKS_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(TASKS_CACHE_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.get(cacheKey);
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({
            tasks: result.tasks,
            pagination: result.pagination,
            timestamp: result.timestamp,
          });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting tasks cache:', error);
    return null;
  }
};

/**
 * Clear old tasks cache (older than 7 days)
 */
export const clearOldTasksCache = async () => {
  try {
    const db = await initTasksCacheDB();
    const transaction = db.transaction([TASKS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(TASKS_CACHE_STORE);
    const index = store.index('timestamp');
    
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.upperBound(sevenDaysAgo));
      let deletedCount = 0;
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          deletedCount++;
          cursor.continue();
        } else {
          resolve(deletedCount);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error clearing old tasks cache:', error);
    return 0;
  }
};

/**
 * Generate cache key from query parameters
 */
export const generateTasksCacheKey = (params) => {
  const { page, per_page, search, project_id, status, employee_id } = params;
  return `tasks_${page || 1}_${per_page || 15}_${search || ''}_${project_id || ''}_${status || ''}_${employee_id || ''}`;
};

/**
 * Generate general cache key (without page number) for fallback
 */
export const generateGeneralTasksCacheKey = (params) => {
  const { per_page, search, project_id, status, employee_id } = params;
  return `tasks_general_${per_page || 15}_${search || ''}_${project_id || ''}_${status || ''}_${employee_id || ''}`;
};

/**
 * Get all tasks cache entries for a specific query (all pages)
 */
export const getAllTasksCacheForQuery = async (params) => {
  try {
    const db = await initTasksCacheDB();
    const transaction = db.transaction([TASKS_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(TASKS_CACHE_STORE);
    
    const { per_page, search, project_id, status, employee_id } = params;
    // Match cache keys that start with tasks_ and have the same query params (but different page numbers)
    const queryBase = `${per_page || 15}_${search || ''}_${project_id || ''}_${status || ''}_${employee_id || ''}`;
    
    return new Promise((resolve, reject) => {
      const allTasks = [];
      const request = store.openCursor();
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const key = cursor.key;
          if (typeof key === 'string' && key.startsWith('tasks_')) {
            // Extract the query part (everything after tasks_ and before the page number)
            // Format: tasks_{page}_{per_page}_{search}_{project_id}_{status}_{employee_id}
            const parts = key.split('_');
            if (parts.length >= 6) {
              // Skip 'tasks' and page number, get the rest
              const keyQueryBase = parts.slice(2).join('_');
              if (keyQueryBase === queryBase) {
                const tasks = cursor.value.tasks || [];
                allTasks.push(...tasks);
              }
            }
          }
          cursor.continue();
        } else {
          // Remove duplicates based on task id
          const uniqueTasks = Array.from(
            new Map(allTasks.map(task => [task.id, task])).values()
          );
          resolve(uniqueTasks);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting all tasks cache for query:', error);
    return [];
  }
};

/**
 * Store name for vendors cache
 */
const VENDORS_CACHE_STORE = 'vendorsCache';

/**
 * Initialize vendors cache store
 */
const initVendorsCacheDB = async () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('OfflineVendorsDB', 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(VENDORS_CACHE_STORE)) {
        const store = db.createObjectStore(VENDORS_CACHE_STORE, { keyPath: 'cacheKey' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

/**
 * Save vendors data to IndexedDB cache
 */
export const saveVendorsCache = async (cacheKey, vendorsData, pagination) => {
  try {
    const db = await initVendorsCacheDB();
    const transaction = db.transaction([VENDORS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(VENDORS_CACHE_STORE);
    
    const cacheData = {
      cacheKey,
      vendors: vendorsData,
      pagination,
      timestamp: Date.now(),
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(cacheData);
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error saving vendors cache:', error);
    throw error;
  }
};

/**
 * Get vendors data from IndexedDB cache
 */
export const getVendorsCache = async (cacheKey) => {
  try {
    const db = await initVendorsCacheDB();
    const transaction = db.transaction([VENDORS_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(VENDORS_CACHE_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.get(cacheKey);
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({
            vendors: result.vendors,
            pagination: result.pagination,
            timestamp: result.timestamp,
          });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting vendors cache:', error);
    return null;
  }
};

/**
 * Clear old vendors cache (older than 7 days)
 */
export const clearOldVendorsCache = async () => {
  try {
    const db = await initVendorsCacheDB();
    const transaction = db.transaction([VENDORS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(VENDORS_CACHE_STORE);
    const index = store.index('timestamp');
    
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.upperBound(sevenDaysAgo));
      let deletedCount = 0;
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          deletedCount++;
          cursor.continue();
        } else {
          resolve(deletedCount);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error clearing old vendors cache:', error);
    return 0;
  }
};

/**
 * Generate cache key from query parameters for vendors
 */
export const generateVendorsCacheKey = (params) => {
  const { page, per_page, search } = params;
  return `vendors_${page || 1}_${per_page || 10}_${search || ''}`;
};

/**
 * Get all vendors cache entries for a specific query (all pages)
 */
export const getAllVendorsCacheForQuery = async (params) => {
  try {
    const db = await initVendorsCacheDB();
    const transaction = db.transaction([VENDORS_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(VENDORS_CACHE_STORE);
    
    const { per_page, search } = params;
    // Match cache keys that start with vendors_ and have the same query params (but different page numbers)
    const queryBase = `${per_page || 10}_${search || ''}`;
    
    return new Promise((resolve, reject) => {
      const allVendors = [];
      const request = store.openCursor();
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const key = cursor.key;
          if (typeof key === 'string' && key.startsWith('vendors_')) {
            // Extract the query part (everything after vendors_ and before the page number)
            // Format: vendors_{page}_{per_page}_{search}
            const parts = key.split('_');
            if (parts.length >= 4) {
              // Skip 'vendors' and page number, get the rest
              const keyQueryBase = parts.slice(2).join('_');
              if (keyQueryBase === queryBase) {
                const vendors = cursor.value.vendors || [];
                allVendors.push(...vendors);
              }
            }
          }
          cursor.continue();
        } else {
          // Remove duplicates based on vendor id
          const uniqueVendors = Array.from(
            new Map(allVendors.map(vendor => [vendor.id, vendor])).values()
          );
          resolve(uniqueVendors);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting all vendors cache for query:', error);
    return [];
  }
};

/**
 * Store name for quotations cache
 */
const QUOTATIONS_CACHE_STORE = 'quotationsCache';

/**
 * Initialize quotations cache store
 */
const initQuotationsCacheDB = async () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('OfflineQuotationsDB', 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(QUOTATIONS_CACHE_STORE)) {
        const store = db.createObjectStore(QUOTATIONS_CACHE_STORE, { keyPath: 'cacheKey' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

/**
 * Save quotations data to IndexedDB cache
 */
export const saveQuotationsCache = async (cacheKey, quotationsData, pagination) => {
  try {
    const db = await initQuotationsCacheDB();
    const transaction = db.transaction([QUOTATIONS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(QUOTATIONS_CACHE_STORE);
    
    const cacheData = {
      cacheKey,
      quotations: quotationsData,
      pagination,
      timestamp: Date.now(),
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(cacheData);
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error saving quotations cache:', error);
    throw error;
  }
};

/**
 * Get quotations data from IndexedDB cache
 */
export const getQuotationsCache = async (cacheKey) => {
  try {
    const db = await initQuotationsCacheDB();
    const transaction = db.transaction([QUOTATIONS_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(QUOTATIONS_CACHE_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.get(cacheKey);
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({
            quotations: result.quotations,
            pagination: result.pagination,
            timestamp: result.timestamp,
          });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting quotations cache:', error);
    return null;
  }
};

/**
 * Clear old quotations cache (older than 7 days)
 */
export const clearOldQuotationsCache = async () => {
  try {
    const db = await initQuotationsCacheDB();
    const transaction = db.transaction([QUOTATIONS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(QUOTATIONS_CACHE_STORE);
    const index = store.index('timestamp');
    
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.upperBound(sevenDaysAgo));
      let deletedCount = 0;
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          deletedCount++;
          cursor.continue();
        } else {
          resolve(deletedCount);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error clearing old quotations cache:', error);
    return 0;
  }
};

/**
 * Generate cache key from query parameters for quotations
 */
export const generateQuotationsCacheKey = (params) => {
  const { page, per_page, search } = params;
  return `quotations_${page || 1}_${per_page || 10}_${search || ''}`;
};

/**
 * Get all quotations cache entries for a specific query (all pages)
 */
export const getAllQuotationsCacheForQuery = async (params) => {
  try {
    const db = await initQuotationsCacheDB();
    const transaction = db.transaction([QUOTATIONS_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(QUOTATIONS_CACHE_STORE);
    
    const { per_page, search } = params;
    // Match cache keys that start with quotations_ and have the same query params (but different page numbers)
    const queryBase = `${per_page || 10}_${search || ''}`;
    
    return new Promise((resolve, reject) => {
      const allQuotations = [];
      const request = store.openCursor();
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const key = cursor.key;
          if (typeof key === 'string' && key.startsWith('quotations_')) {
            // Extract the query part (everything after quotations_ and before the page number)
            // Format: quotations_{page}_{per_page}_{search}
            const parts = key.split('_');
            if (parts.length >= 4) {
              // Skip 'quotations' and page number, get the rest
              const keyQueryBase = parts.slice(2).join('_');
              if (keyQueryBase === queryBase) {
                const quotations = cursor.value.quotations || [];
                allQuotations.push(...quotations);
              }
            }
          }
          cursor.continue();
        } else {
          // Remove duplicates based on quotation id
          const uniqueQuotations = Array.from(
            new Map(allQuotations.map(quotation => [quotation.id, quotation])).values()
          );
          resolve(uniqueQuotations);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting all quotations cache for query:', error);
    return [];
  }
};

/**
 * Remove a vendor from all cache entries
 */
export const removeVendorFromCache = async (vendorId) => {
  try {
    const db = await initVendorsCacheDB();
    const transaction = db.transaction([VENDORS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(VENDORS_CACHE_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.openCursor();
      let updatedCount = 0;
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const key = cursor.key;
          if (typeof key === 'string' && key.startsWith('vendors_')) {
            const cacheData = cursor.value;
            if (cacheData && Array.isArray(cacheData.vendors)) {
              // Remove the vendor from this cache entry
              const filteredVendors = cacheData.vendors.filter(
                vendor => vendor.id !== vendorId
              );
              
              // Only update if the vendor was actually in this cache entry
              if (filteredVendors.length !== cacheData.vendors.length) {
                cacheData.vendors = filteredVendors;
                
                // Update pagination total if it exists
                if (cacheData.pagination && cacheData.pagination.total) {
                  cacheData.pagination.total = Math.max(0, cacheData.pagination.total - 1);
                }
                
                // Update the cache entry
                const updateRequest = cursor.update(cacheData);
                updateRequest.onsuccess = () => {
                  updatedCount++;
                  cursor.continue();
                };
                updateRequest.onerror = () => {
                  console.error('Error updating cache entry:', updateRequest.error);
                  cursor.continue();
                };
              } else {
                cursor.continue();
              }
            } else {
              cursor.continue();
            }
          } else {
            cursor.continue();
          }
        } else {
          resolve(updatedCount);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error removing vendor from cache:', error);
    return 0;
  }
};

/**
 * Remove a quotation from all cache entries
 */
export const removeQuotationFromCache = async (quotationId) => {
  try {
    const db = await initQuotationsCacheDB();
    const transaction = db.transaction([QUOTATIONS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(QUOTATIONS_CACHE_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.openCursor();
      let updatedCount = 0;
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const key = cursor.key;
          if (typeof key === 'string' && key.startsWith('quotations_')) {
            const cacheData = cursor.value;
            if (cacheData && Array.isArray(cacheData.quotations)) {
              // Remove the quotation from this cache entry
              const filteredQuotations = cacheData.quotations.filter(
                quotation => quotation.id !== quotationId
              );
              
              // Only update if the quotation was actually in this cache entry
              if (filteredQuotations.length !== cacheData.quotations.length) {
                cacheData.quotations = filteredQuotations;
                
                // Update pagination total if it exists
                if (cacheData.pagination && cacheData.pagination.total) {
                  cacheData.pagination.total = Math.max(0, cacheData.pagination.total - 1);
                }
                
                // Update the cache entry
                const updateRequest = cursor.update(cacheData);
                updateRequest.onsuccess = () => {
                  updatedCount++;
                  cursor.continue();
                };
                updateRequest.onerror = () => {
                  console.error('Error updating cache entry:', updateRequest.error);
                  cursor.continue();
                };
              } else {
                cursor.continue();
              }
            } else {
              cursor.continue();
            }
          } else {
            cursor.continue();
          }
        } else {
          resolve(updatedCount);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error removing quotation from cache:', error);
    return 0;
  }
};

/**
 * Store name for dropdowns cache (projects, employees, lab tests)
 */
const DROPDOWNS_CACHE_STORE = 'dropdownsCache';

/**
 * Initialize dropdowns cache store
 */
const initDropdownsCacheDB = async () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('OfflineDropdownsDB', 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(DROPDOWNS_CACHE_STORE)) {
        const store = db.createObjectStore(DROPDOWNS_CACHE_STORE, { keyPath: 'cacheKey' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

/**
 * Save dropdown data to IndexedDB cache
 */
export const saveDropdownCache = async (cacheKey, items, pagination) => {
  try {
    const db = await initDropdownsCacheDB();
    const transaction = db.transaction([DROPDOWNS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(DROPDOWNS_CACHE_STORE);
    
    const cacheData = {
      cacheKey,
      items,
      pagination,
      timestamp: Date.now(),
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(cacheData);
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error saving dropdown cache:', error);
    throw error;
  }
};

/**
 * Get dropdown data from IndexedDB cache
 */
export const getDropdownCache = async (cacheKey) => {
  try {
    const db = await initDropdownsCacheDB();
    const transaction = db.transaction([DROPDOWNS_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(DROPDOWNS_CACHE_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.get(cacheKey);
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({
            items: result.items,
            pagination: result.pagination,
            timestamp: result.timestamp,
          });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting dropdown cache:', error);
    return null;
  }
};

/**
 * Get all dropdown cache entries for a specific query (all pages)
 */
export const getAllDropdownCacheForQuery = async (type, params) => {
  try {
    const db = await initDropdownsCacheDB();
    const transaction = db.transaction([DROPDOWNS_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(DROPDOWNS_CACHE_STORE);
    
    const { per_page, search } = params;
    // Match cache keys that start with {type}_ and have the same query params (but different page numbers)
    const queryBase = `${per_page || 10}_${search || ''}`;
    
    return new Promise((resolve, reject) => {
      const allItems = [];
      const request = store.openCursor();
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const key = cursor.key;
          if (typeof key === 'string' && key.startsWith(`${type}_`)) {
            // Extract the query part (everything after {type}_ and before the page number)
            // Format: {type}_{page}_{per_page}_{search}
            const parts = key.split('_');
            if (parts.length >= 4) {
              // Skip type and page number, get the rest
              const keyQueryBase = parts.slice(2).join('_');
              if (keyQueryBase === queryBase) {
                const items = cursor.value.items || [];
                if (Array.isArray(items)) {
                  allItems.push(...items);
                }
              }
            }
          }
          cursor.continue();
        } else {
          // Remove duplicates based on id
          const uniqueItems = Array.from(
            new Map(allItems.map(item => [item.id, item])).values()
          );
          resolve(uniqueItems);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error(`Error getting all ${type} cache for query:`, error);
    return [];
  }

};

/**
 * Get any dropdown cache entries for a specific type (ignoring params)
 * Fallback for when specific query yields no results offline
 */
export const getAnyDropdownCache = async (type) => {
  try {
    const db = await initDropdownsCacheDB();
    const transaction = db.transaction([DROPDOWNS_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(DROPDOWNS_CACHE_STORE);
    
    return new Promise((resolve, reject) => {
      const allItems = [];
      const request = store.openCursor();
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const key = cursor.key;
          if (typeof key === 'string' && key.startsWith(`${type}_`)) {
             const items = cursor.value.items || [];
             if (Array.isArray(items)) {
               allItems.push(...items);
             }
          }
          cursor.continue();
        } else {
          // Remove duplicates based on id
          const uniqueItems = Array.from(
            new Map(allItems.map(item => [item.id, item])).values()
          );
          resolve(uniqueItems);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error(`Error getting any ${type} cache:`, error);
    return [];
  }
};

/**
 * Generate cache key from query parameters for dropdowns
 */
export const generateDropdownCacheKey = (type, params) => {
  const { page, per_page, search } = params;
  return `${type}_${page || 1}_${per_page || 10}_${search || ''}`;
};

/**
 * Store name for customers cache
 */
const CUSTOMERS_CACHE_STORE = 'customersCache';

/**
 * Initialize customers cache store
 */
const initCustomersCacheDB = async () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('OfflineCustomersDB', 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(CUSTOMERS_CACHE_STORE)) {
        const store = db.createObjectStore(CUSTOMERS_CACHE_STORE, { keyPath: 'cacheKey' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

/**
 * Save customers data to IndexedDB cache
 */
export const saveCustomersCache = async (cacheKey, customersData, pagination) => {
  try {
    const db = await initCustomersCacheDB();
    const transaction = db.transaction([CUSTOMERS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(CUSTOMERS_CACHE_STORE);
    
    const cacheData = {
      cacheKey,
      customers: customersData,
      pagination,
      timestamp: Date.now(),
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(cacheData);
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error saving customers cache:', error);
    throw error;
  }
};

/**
 * Get customers data from IndexedDB cache
 */
export const getCustomersCache = async (cacheKey) => {
  try {
    const db = await initCustomersCacheDB();
    const transaction = db.transaction([CUSTOMERS_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(CUSTOMERS_CACHE_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.get(cacheKey);
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({
            customers: result.customers,
            pagination: result.pagination,
            timestamp: result.timestamp,
          });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting customers cache:', error);
    return null;
  }
};

/**
 * Clear old customers cache (older than 7 days)
 */
export const clearOldCustomersCache = async () => {
  try {
    const db = await initCustomersCacheDB();
    const transaction = db.transaction([CUSTOMERS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(CUSTOMERS_CACHE_STORE);
    const index = store.index('timestamp');
    
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.upperBound(sevenDaysAgo));
      let deletedCount = 0;
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          deletedCount++;
          cursor.continue();
        } else {
          resolve(deletedCount);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error clearing old customers cache:', error);
    return 0;
  }
};

/**
 * Generate cache key from query parameters for customers
 */
export const generateCustomersCacheKey = (params) => {
  const { page, per_page, search, is_active } = params;
  return `customers_${page || 1}_${per_page || 10}_${search || ''}_${is_active !== undefined ? is_active : ''}`;
};

/**
 * Get all customers cache entries for a specific query (all pages)
 */
export const getAllCustomersCacheForQuery = async (params) => {
  try {
    const db = await initCustomersCacheDB();
    const transaction = db.transaction([CUSTOMERS_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(CUSTOMERS_CACHE_STORE);
    
    const { per_page, search, is_active } = params;
    // Match cache keys that start with customers_ and have the same query params (but different page numbers)
    const queryBase = `${per_page || 10}_${search || ''}_${is_active !== undefined ? is_active : ''}`;
    
    return new Promise((resolve, reject) => {
      const allCustomers = [];
      const request = store.openCursor();
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const key = cursor.key;
          if (typeof key === 'string' && key.startsWith('customers_')) {
            // Extract the query part (everything after customers_ and before the page number)
            // Format: customers_{page}_{per_page}_{search}_{is_active}
            const parts = key.split('_');
            if (parts.length >= 5) {
              // Skip 'customers' and page number, get the rest
              const keyQueryBase = parts.slice(2).join('_');
              if (keyQueryBase === queryBase) {
                const customers = cursor.value.customers || [];
                if (Array.isArray(customers)) {
                  allCustomers.push(...customers);
                }
              }
            }
          }
          cursor.continue();
        } else {
          // Remove duplicates based on customer id
          const uniqueCustomers = Array.from(
            new Map(allCustomers.map(customer => [customer.id, customer])).values()
          );
          resolve(uniqueCustomers);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting all customers cache for query:', error);
    return [];
  }
};

/**
 * Remove a customer from all cache entries
 */
export const removeCustomerFromCache = async (customerId) => {
  try {
    const db = await initCustomersCacheDB();
    const transaction = db.transaction([CUSTOMERS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(CUSTOMERS_CACHE_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.openCursor();
      let updatedCount = 0;
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const key = cursor.key;
          if (typeof key === 'string' && key.startsWith('customers_')) {
            const cacheData = cursor.value;
            if (cacheData && Array.isArray(cacheData.customers)) {
              // Remove the customer from this cache entry
              const filteredCustomers = cacheData.customers.filter(
                customer => customer.id !== customerId
              );
              
              // Only update if the customer was actually in this cache entry
              if (filteredCustomers.length !== cacheData.customers.length) {
                cacheData.customers = filteredCustomers;
                
                // Update pagination total if it exists
                if (cacheData.pagination && cacheData.pagination.total) {
                  cacheData.pagination.total = Math.max(0, cacheData.pagination.total - 1);
                }
                
                // Update the cache entry
                const updateRequest = cursor.update(cacheData);
                updateRequest.onsuccess = () => {
                  updatedCount++;
                  cursor.continue();
                };
                updateRequest.onerror = () => {
                  console.error('Error updating cache entry:', updateRequest.error);
                  cursor.continue();
                };
              } else {
                cursor.continue();
              }
            } else {
              cursor.continue();
            }
          } else {
            cursor.continue();
          }
        } else {
          resolve(updatedCount);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error removing customer from cache:', error);
    return 0;
  }
};

/**
 * Remove a task from all cache entries
 */
export const removeTaskFromCache = async (taskId) => {
  try {
    const db = await initTasksCacheDB();
    const transaction = db.transaction([TASKS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(TASKS_CACHE_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.openCursor();
      let updatedCount = 0;
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const key = cursor.key;
          if (typeof key === 'string' && key.startsWith('tasks_')) {
            const cacheData = cursor.value;
            if (cacheData && Array.isArray(cacheData.tasks)) {
              // Remove the task from this cache entry
              const filteredTasks = cacheData.tasks.filter(
                task => task.id !== taskId
              );
              
              // Only update if the task was actually in this cache entry
              if (filteredTasks.length !== cacheData.tasks.length) {
                cacheData.tasks = filteredTasks;
                
                // Update pagination total if it exists
                if (cacheData.pagination && cacheData.pagination.total) {
                  cacheData.pagination.total = Math.max(0, cacheData.pagination.total - 1);
                }
                
                // Update the cache entry
                const updateRequest = cursor.update(cacheData);
                updateRequest.onsuccess = () => {
                  updatedCount++;
                  cursor.continue();
                };
                updateRequest.onerror = () => {
                  console.error('Error updating cache entry:', updateRequest.error);
                  cursor.continue();
                };
              } else {
                cursor.continue();
              }
            } else {
              cursor.continue();
            }
          } else {
            cursor.continue();
          }
        } else {
          resolve(updatedCount);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error removing task from cache:', error);
    return 0;
  }
};

/**
 * Clear old contracts cache (older than 7 days)
 */
export const clearOldContractsCache = async () => {
  try {
    const db = await initContractsCacheDB();
    const transaction = db.transaction([CONTRACTS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(CONTRACTS_CACHE_STORE);
    const index = store.index('timestamp');
    
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.upperBound(sevenDaysAgo));
      let deletedCount = 0;
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          deletedCount++;
          cursor.continue();
        } else {
          resolve(deletedCount);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error clearing old contracts cache:', error);
    return 0;
  }
};

/**
 * Remove a contract from all cache entries
 */
export const removeContractFromCache = async (contractId) => {
  try {
    const db = await initContractsCacheDB();
    const transaction = db.transaction([CONTRACTS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(CONTRACTS_CACHE_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.openCursor();
      let updatedCount = 0;
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const key = cursor.key;
          if (typeof key === 'string' && key.startsWith('contracts_')) {
            const cacheData = cursor.value;
            if (cacheData && Array.isArray(cacheData.contracts)) {
              // Remove the contract from this cache entry
              const filteredContracts = cacheData.contracts.filter(
                contract => contract.id !== contractId
              );
              
              // Only update if the contract was actually in this cache entry
              if (filteredContracts.length !== cacheData.contracts.length) {
                cacheData.contracts = filteredContracts;
                
                // Update pagination total if it exists
                if (cacheData.pagination && cacheData.pagination.total) {
                  cacheData.pagination.total = Math.max(0, cacheData.pagination.total - 1);
                }
                
                // Update the cache entry
                const updateRequest = cursor.update(cacheData);
                updateRequest.onsuccess = () => {
                  updatedCount++;
                  cursor.continue();
                };
                updateRequest.onerror = () => {
                  console.error('Error updating cache entry:', updateRequest.error);
                  cursor.continue();
                };
              } else {
                cursor.continue();
              }
            } else {
              cursor.continue();
            }
          } else {
            cursor.continue();
          }
        } else {
          resolve(updatedCount);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error removing contract from cache:', error);
    return 0;
  }
};

/**
 * Store name for progress bills cache
 */
const PROGRESS_BILLS_CACHE_STORE = 'progressBillsCache';

/**
 * Initialize progress bills cache store
 */
const initProgressBillsCacheDB = async () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('OfflineProgressBillsDB', 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(PROGRESS_BILLS_CACHE_STORE)) {
        const store = db.createObjectStore(PROGRESS_BILLS_CACHE_STORE, { keyPath: 'cacheKey' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

/**
 * Save progress bill data to IndexedDB cache
 */
export const saveProgressBillCache = async (cacheKey, progressBillsData, pagination) => {
  try {
    const db = await initProgressBillsCacheDB();
    const transaction = db.transaction([PROGRESS_BILLS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(PROGRESS_BILLS_CACHE_STORE);
    
    const cacheData = {
      cacheKey,
      progressBills: progressBillsData,
      pagination,
      timestamp: Date.now(),
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(cacheData);
      request.onsuccess = () => {
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error saving progress bill cache:', error);
    throw error;
  }
};

/**
 * Get progress bill data from IndexedDB cache
 */
export const getProgressBillCache = async (cacheKey) => {
  try {
    const db = await initProgressBillsCacheDB();
    const transaction = db.transaction([PROGRESS_BILLS_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(PROGRESS_BILLS_CACHE_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.get(cacheKey);
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          resolve({
            progressBills: result.progressBills,
            pagination: result.pagination,
            timestamp: result.timestamp,
          });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting progress bill cache:', error);
    return null;
  }
};

/**
 * Clear old progress bills cache (older than 7 days)
 */
export const clearOldProgressBillsCache = async () => {
  try {
    const db = await initProgressBillsCacheDB();
    const transaction = db.transaction([PROGRESS_BILLS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(PROGRESS_BILLS_CACHE_STORE);
    const index = store.index('timestamp');
    
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
    
    return new Promise((resolve, reject) => {
      const request = index.openCursor(IDBKeyRange.upperBound(sevenDaysAgo));
      let deletedCount = 0;
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          cursor.delete();
          deletedCount++;
          cursor.continue();
        } else {
          resolve(deletedCount);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error clearing old progress bills cache:', error);
    return 0;
  }
};

/**
 * Generate cache key from query parameters for progress bills
 */
export const generateProgressBillsCacheKey = (params) => {
  const { page, per_page, search, contract_id } = params;
  return `progress_bills_${page || 1}_${per_page || 10}_${search || ''}_${contract_id || ''}`;
};

/**
 * Get all progress bills cache entries for a specific query (all pages)
 */
export const getAllProgressBillsCacheForQuery = async (params) => {
  try {
    const db = await initProgressBillsCacheDB();
    const transaction = db.transaction([PROGRESS_BILLS_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(PROGRESS_BILLS_CACHE_STORE);
    
    const { per_page, search, contract_id } = params;
    // Match cache keys that start with progress_bills_ and have the same query params (but different page numbers)
    const queryBase = `${per_page || 10}_${search || ''}_${contract_id || ''}`;
    
    return new Promise((resolve, reject) => {
      const allProgressBills = [];
      const request = store.openCursor();
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const key = cursor.key;
          if (typeof key === 'string' && key.startsWith('progress_bills_')) {
            // Extract the query part
            const parts = key.split('_');
            if (parts.length >= 6) {
              // Skip 'progress', 'bills' and page number, get the rest
              const keyQueryBase = parts.slice(3).join('_');
              if (keyQueryBase === queryBase) {
                const bills = cursor.value.progressBills || [];
                if (Array.isArray(bills)) {
                  allProgressBills.push(...bills);
                }
              }
            }
          }
          cursor.continue();
        } else {
          // Remove duplicates based on id
          const uniqueBills = Array.from(
            new Map(allProgressBills.map(bill => [bill.id, bill])).values()
          );
          resolve(uniqueBills);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting all progress bills cache for query:', error);
    return [];
  }
};

/**
 * Remove a progress bill from all cache entries
 */
export const removeProgressBillFromCache = async (billId) => {
  try {
    const db = await initProgressBillsCacheDB();
    const transaction = db.transaction([PROGRESS_BILLS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(PROGRESS_BILLS_CACHE_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.openCursor();
      let updatedCount = 0;
      
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          const key = cursor.key;
          if (typeof key === 'string' && key.startsWith('progress_bills_')) {
            const cacheData = cursor.value;
            if (cacheData && Array.isArray(cacheData.progressBills)) {
              // Remove the bill from this cache entry
              const filteredBills = cacheData.progressBills.filter(
                bill => bill.id !== billId
              );
              
              // Only update if the bill was actually in this cache entry
              if (filteredBills.length !== cacheData.progressBills.length) {
                cacheData.progressBills = filteredBills;
                
                // Update pagination total if it exists
                if (cacheData.pagination && cacheData.pagination.total) {
                  cacheData.pagination.total = Math.max(0, cacheData.pagination.total - 1);
                }
                
                // Update the cache entry
                const updateRequest = cursor.update(cacheData);
                updateRequest.onsuccess = () => {
                  updatedCount++;
                  cursor.continue();
                };
                updateRequest.onerror = () => {
                  console.error('Error updating cache entry:', updateRequest.error);
                  cursor.continue();
                };
              } else {
                cursor.continue();
              }
            } else {
              cursor.continue();
            }
          } else {
            cursor.continue();
          }
        } else {
          resolve(updatedCount);
        }
      };
      
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error removing progress bill from cache:', error);
    return 0;
  }
};

/**
 * Store name for progress bill details cache
 */
const PROGRESS_BILL_DETAILS_CACHE_STORE = 'progressBillDetailsCache';

/**
 * Initialize progress bill details cache store
 */
const initProgressBillDetailsCacheDB = async () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('OfflineProgressBillDetailsDB', 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(PROGRESS_BILL_DETAILS_CACHE_STORE)) {
        const store = db.createObjectStore(PROGRESS_BILL_DETAILS_CACHE_STORE, { keyPath: 'id' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

/**
 * Save progress bill detail to IndexedDB cache
 */
export const saveProgressBillDetailCache = async (id, data) => {
  try {
    const db = await initProgressBillDetailsCacheDB();
    const transaction = db.transaction([PROGRESS_BILL_DETAILS_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(PROGRESS_BILL_DETAILS_CACHE_STORE);
    
    const cacheData = {
      id: String(id),
      data,
      timestamp: Date.now(),
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(cacheData);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error saving progress bill detail cache:', error);
    throw error;
  }
};

/**
 * Get progress bill detail from IndexedDB cache
 */
export const getProgressBillDetailCache = async (id) => {
  try {
    const db = await initProgressBillDetailsCacheDB();
    const transaction = db.transaction([PROGRESS_BILL_DETAILS_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(PROGRESS_BILL_DETAILS_CACHE_STORE);
    
    return new Promise((resolve, reject) => {
      const request = store.get(String(id));
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.data : null);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting progress bill detail cache:', error);
    return null;
  }
};

/**
 * Store name for generic response cache
 */
const GENERIC_RESPONSE_CACHE_STORE = 'genericResponseCache';

/**
 * Initialize generic response cache store
 */
const initGenericResponseCacheDB = async () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('OfflineGenericResponseDB', 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(GENERIC_RESPONSE_CACHE_STORE)) {
        const store = db.createObjectStore(GENERIC_RESPONSE_CACHE_STORE, { keyPath: 'key' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

/**
 * Generate generic cache key from URL and params
 */
export const generateGenericCacheKey = (url, params) => {
  try {
    // 1. Convert to full URL object for parsing (using dummy base for relative paths)
    const base = url.startsWith('http') ? undefined : 'http://localhost';
    const urlObj = new URL(url, base);
    
    // 2. Extract only the path (ignoring domain and protocol)
    const path = urlObj.pathname.toLowerCase();
    
    // 3. Merge and sort params
    const finalParams = new URLSearchParams(urlObj.search);
    if (params) {
      const extraParams = new URLSearchParams(params);
      extraParams.forEach((v, k) => finalParams.set(k, v));
    }
    finalParams.sort();
    
    // 4. Clean up path: remove BASE_URL parts if they exist to keep keys clean
    // This ensures that /public/api/customers and /customers point to the same key
    const cleanPath = path.split('/').filter(p => p && p !== 'public' && p !== 'api').join('/');
    
    const queryString = finalParams.toString();
    return queryString ? `${cleanPath}?${queryString}` : cleanPath;
  } catch (e) {
    console.warn('[CacheKey] Fallback for:', url);
    return url;
  }
};

/**
 * Save generic response to IndexedDB cache
 */
export const saveGenericResponseCache = async (url, params, data) => {
  try {
    const db = await initGenericResponseCacheDB();
    const transaction = db.transaction([GENERIC_RESPONSE_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(GENERIC_RESPONSE_CACHE_STORE);
    
    const key = generateGenericCacheKey(url, params);
    
    const cacheData = {
      key,
      data,
      timestamp: Date.now(),
    };
    
    return new Promise((resolve, reject) => {
      const request = store.put(cacheData);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error saving generic response cache:', error);
    // Don't throw, just log
  }
};

/**
 * Get generic response from IndexedDB cache
 */
export const getGenericResponseCache = async (url, params) => {
  try {
    const db = await initGenericResponseCacheDB();
    const transaction = db.transaction([GENERIC_RESPONSE_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(GENERIC_RESPONSE_CACHE_STORE);
    
    const key = generateGenericCacheKey(url, params);
    
    return new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.data : null);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting generic response cache:', error);
    return null;
  }
};

/**
 * Store name for penalty types cache
 */
const PENALTY_TYPES_CACHE_STORE = 'penaltyTypesCache';

/**
 * Initialize penalty types cache store
 */
const initPenaltyTypesCacheDB = async () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open('PenaltyTypesCacheDB', 1);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(PENALTY_TYPES_CACHE_STORE)) {
        const store = db.createObjectStore(PENALTY_TYPES_CACHE_STORE, { keyPath: 'cacheKey' });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
    };
  });
};

/**
 * Save penalty types data to IndexedDB cache
 */
export const savePenaltyTypesCache = async (cacheKey, penaltyTypesData, pagination) => {
  try {
    const db = await initPenaltyTypesCacheDB();
    const transaction = db.transaction([PENALTY_TYPES_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(PENALTY_TYPES_CACHE_STORE);

    const cacheData = {
      cacheKey,
      data: penaltyTypesData,
      pagination,
      timestamp: Date.now(),
    };

    return new Promise((resolve, reject) => {
      const request = store.put(cacheData);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error saving penalty types cache:', error);
  }
};

/**
 * Get penalty types data from IndexedDB cache
 */
export const getPenaltyTypesCache = async (cacheKey) => {
  try {
    const db = await initPenaltyTypesCacheDB();
    const transaction = db.transaction([PENALTY_TYPES_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(PENALTY_TYPES_CACHE_STORE);

    return new Promise((resolve, reject) => {
      const request = store.get(cacheKey);
      request.onsuccess = () => {
        const result = request.result;
        if (result && Date.now() - result.timestamp < 7 * 24 * 60 * 60 * 1000) {
          resolve({
            data: result.data,
            pagination: result.pagination,
          });
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting penalty types cache:', error);
    return null;
  }
};

/**
 * Generate cache key from query parameters for penalty types
 */
export const generatePenaltyTypesCacheKey = (params) => {
  const { page, per_page, search, is_active } = params;
  return `penalty_types_${page || 1}_${per_page || 10}_${search || ''}_${is_active !== undefined ? is_active : ''}`;
};

/**
 * Get all penalty types cache entries for a specific query (all pages)
 */
export const getAllPenaltyTypesCacheForQuery = async (params) => {
  try {
    const db = await initPenaltyTypesCacheDB();
    const transaction = db.transaction([PENALTY_TYPES_CACHE_STORE], 'readonly');
    const store = transaction.objectStore(PENALTY_TYPES_CACHE_STORE);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const allCached = request.result;
        const { search, is_active } = params;
        
        const matchingCaches = allCached.filter(cache => {
          if (Date.now() - cache.timestamp > 7 * 24 * 60 * 60 * 1000) return false;
          
          const parts = cache.cacheKey.split('_');
          const cachedSearch = parts[4] || '';
          const cachedIsActive = parts[5] || '';
          
          return cachedSearch === (search || '') && cachedIsActive === (is_active !== undefined ? String(is_active) : '');
        });

        if (matchingCaches.length === 0) {
          resolve(null);
          return;
        }

        const allPenaltyTypes = [];
        matchingCaches.forEach(cache => {
          if (cache.data && Array.isArray(cache.data)) {
            allPenaltyTypes.push(...cache.data);
          }
        });

        const uniquePenaltyTypes = Array.from(
          new Map(allPenaltyTypes.map(pt => [pt.id, pt])).values()
        );

        resolve({
          data: uniquePenaltyTypes,
          pagination: matchingCaches[0]?.pagination || {},
        });
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error getting all penalty types cache:', error);
    return null;
  }
};

/**
 * Remove a penalty type from all cache entries
 */
export const removePenaltyTypeFromCache = async (penaltyTypeId) => {
  try {
    const db = await initPenaltyTypesCacheDB();
    const transaction = db.transaction([PENALTY_TYPES_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(PENALTY_TYPES_CACHE_STORE);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        const allCaches = request.result;
        
        allCaches.forEach(cache => {
          if (cache.data && Array.isArray(cache.data)) {
            const filteredData = cache.data.filter(pt => pt.id !== penaltyTypeId);
            
            if (filteredData.length !== cache.data.length) {
              cache.data = filteredData;
              
              if (cache.pagination && cache.pagination.total) {
                cache.pagination.total -= 1;
              }
              
              store.put(cache);
            }
          }
        });
        
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error removing penalty type from cache:', error);
  }
};

/**
 * Clear old penalty types cache (older than 7 days)
 */
export const clearOldPenaltyTypesCache = async () => {
  try {
    const db = await initPenaltyTypesCacheDB();
    const transaction = db.transaction([PENALTY_TYPES_CACHE_STORE], 'readwrite');
    const store = transaction.objectStore(PENALTY_TYPES_CACHE_STORE);
    const index = store.index('timestamp');
    
    const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);

    return new Promise((resolve, reject) => {
      const request = index.openCursor();
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (cursor.value.timestamp < sevenDaysAgo) {
            cursor.delete();
          }
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('Error clearing old penalty types cache:', error);
  }
};

